import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import {
  filter,
  noop,
  Observable,
  retryWhen,
  Subject,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs';

import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';

import {
  Account,
  LeverageSettings,
} from '@personal-area/global/accounts/models';
import { AccountService } from '@personal-area/global/accounts/services';
import { retryStrategy } from '@personal-area/global/http/services';
import { I18nService } from '@personal-area/global/i18n';

import { getAccountType } from '../../pipes/account-type.pipe';
import {
  LeverageModalData,
  LeveragePopupComponent,
} from '../leverage-popup/leverage-popup.component';

interface LeverageComponentState {
  settings: LeverageSettings;
  isChangeDisabled: boolean;
}

const SETTINGS_FALLBACK: LeverageSettings = {
  current: 0,
  isLocked: true,
  availables: {},
  isAvailableChange: false,
  max: 100,
  hasOpenOrders: false,
  nextChangeTime: new Date(),
  nextChangeTimeUTC: Date.now(),
};

@Injectable()
export class LeverageComponentStore extends ComponentStore<LeverageComponentState> {
  public readonly settings$ = this.select((state) => state.settings);
  public readonly isChangeDisabled$ = this.select(
    (state) => state.isChangeDisabled,
  );

  public readonly getLeverage = this.effect((account$: Observable<Account>) =>
    account$.pipe(
      tap(() => {
        this._unsubscribe$.next();
        this.setState({
          isChangeDisabled: true,
          settings: { ...SETTINGS_FALLBACK },
        });
      }),
      switchMap((account) =>
        this._accountService.getLeverageSettings(account.id).pipe(
          retryWhen(retryStrategy()),
          tapResponse(
            (settings) =>
              this.setState({
                isChangeDisabled:
                  !settings.isAvailableChange ||
                  settings.isLocked ||
                  getAccountType(account) === 'archived',
                settings,
              }),
            noop,
          ),
          takeUntil(this._unsubscribe$),
        ),
      ),
    ),
  );

  public readonly changeLeverage = this.effect(
    (account$: Observable<Account>) => {
      const openDialog = ([account, settings]: [Account, LeverageSettings]) =>
        this._dialog
          .open(LeveragePopupComponent, <MatDialogConfig<LeverageModalData>>{
            data: {
              currentLeverage: +settings.current,
              leverages: settings.availables,
              account: account,
              leverageSetting: settings,
              language: this._i18nService.languageA2,
            },
            direction: this._i18nService.isRtl ? 'rtl' : 'ltr',
            panelClass: 'popup-container-360',
          })
          .afterClosed();

      return account$.pipe(
        withLatestFrom(this.settings$),
        switchMap(openDialog),
        filter(Boolean),
        tap(() => this.patchState({ isChangeDisabled: true })),
      );
    },
  );

  private _unsubscribe$ = new Subject<void>();

  constructor(
    private _accountService: AccountService,
    private _dialog: MatDialog,
    private _i18nService: I18nService,
  ) {
    super({
      settings: SETTINGS_FALLBACK,
      isChangeDisabled: true,
    });
  }
}
